import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, PhoneCall, Search } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/constants";
import { EmptyState, LoadingBlock, PageHeader } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { TicketList } from "@/components/followups/TicketList";
import { TicketDetail } from "@/components/followups/TicketDetail";

const STAGES = [["outlet", "1 · Call outlet"], ["customer", "2 · Call customer"], ["done", "Closed"]];

const StageTabs = ({ stage, counts, onChange }) => (
  <div className="no-scrollbar mb-4 flex gap-2 overflow-x-auto" data-testid="stage-tabs">
    {STAGES.map(([k, l]) => (
      <button key={k} onClick={() => onChange(k)} data-testid={`stage-tab-${k}`}
        className={`flex h-10 shrink-0 items-center gap-2 rounded-full border px-4 text-sm font-medium transition-colors ${stage === k ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"}`}>
        {l}<span data-testid={`stage-count-${k}`} className={`rounded-full px-2 text-xs ${stage === k ? "bg-white/20" : "bg-slate-100"}`}>{counts?.[k] ?? "…"}</span>
      </button>
    ))}
  </div>
);

export default function Followups() {
  const { filters } = useApp();
  const { can } = useAuth();
  const [stage, setStage] = useState("outlet");
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [ticket, setTicket] = useState(null);
  const brand = filters.brand.join("|");

  useEffect(() => {
    const id = setTimeout(() => setQ(qInput), 350);
    return () => clearTimeout(id);
  }, [qInput]);
  useEffect(() => {
    setPage(1);
  }, [stage, brand, q]);

  const load = useCallback(() => {
    api.get("/followups", { params: { stage, page, brand: brand || undefined, q: q || undefined } })
      .then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [stage, page, brand, q]);
  useEffect(() => {
    load();
  }, [load]);

  const onSaved = (rec) => {
    setTicket(rec);
    load();
  };
  const items = data?.items || [];
  const pages = data ? Math.max(1, Math.ceil(data.counts[stage] / data.page_size)) : 1;

  return (
    <div data-testid="followups-page">
      <PageHeader title="Follow-ups" subtitle={`Every negative feedback with a phone number${data ? ` since ${fmtDate(data.from)}` : ""}: call the outlet, then the customer. The ticket closes by itself after the customer call.`} />
      <StageTabs stage={stage} counts={data?.counts} onChange={(s) => { setStage(s); setTicket(null); }} />
      <div className="grid gap-6 lg:grid-cols-[380px_minmax(0,1fr)]">
        <div className={ticket ? "hidden lg:block" : ""}>
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input data-testid="followups-search-input" value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="Search name, phone, outlet..." className="h-11 rounded-xl bg-white pl-9" />
          </div>
          {!data ? <LoadingBlock rows={6} /> : !items.length ? (
            <EmptyState icon={PhoneCall} title="Nothing here" text={stage === "done" ? "No closed tickets yet." : "All caught up for this step."} testid="followups-empty" />
          ) : (
            <>
              <TicketList items={items} activeId={ticket?.id} onPick={setTicket} />
              {pages > 1 && (
                <div className="mt-3 flex items-center justify-between text-sm text-slate-500">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="followups-prev"><ChevronLeft className="h-4 w-4" /></Button>
                  <span>Page {page} of {pages}</span>
                  <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="followups-next"><ChevronRight className="h-4 w-4" /></Button>
                </div>
              )}
            </>
          )}
        </div>
        <div className={ticket ? "" : "hidden lg:block"}>
          {ticket && data ? (
            <TicketDetail t={ticket} meta={data} canEdit={can("admin", "manager")} onSaved={onSaved} onBack={() => setTicket(null)} />
          ) : (
            <EmptyState icon={PhoneCall} title="Pick a ticket" text="Choose a customer on the left to start the calls." testid="followups-pick" />
          )}
        </div>
      </div>
    </div>
  );
}
